import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { api, type TraceDetailEvent } from '../api/client';
import { PipelineStrip } from '../components/PipelineStrip';
import {
  CaretRight,
  TreeStructure,
  Clock,
  User,
  Tag,
  Shield,
  Globe,
} from '@phosphor-icons/react';
import {
  buildStepsFromEvents,
  deriveOverallStatus,
  formatToolName,
  getEventColor,
  getEventBgColor,
  getEventSummary,
  getStatusStyle,
  EVENT_LABELS,
} from '../utils/trace-utils';

/** Extract real duration_ms from event metadata, or estimate if not available. */
function extractDurations(events: TraceDetailEvent[]): number[] {
  return events.map((event, i) => {
    // Prefer real duration_ms from metadata (added by pipeline timing)
    const meta = event.metadata;
    if (meta?.duration_ms && typeof meta.duration_ms === 'number') return meta.duration_ms;
    // Check step_timings in TOOL_RESULT_RETURNED events
    if (meta?.step_timings && typeof meta.step_timings === 'object') {
      const timings = meta.step_timings as Record<string, number>;
      const sum = Object.values(timings).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
      if (sum > 0) return sum;
    }
    // Fallback: estimate from timestamps if we have adjacent events
    if (i < events.length - 1) {
      const curr = new Date(event.timestamp).getTime();
      const next = new Date(events[i + 1].timestamp).getTime();
      const diff = next - curr;
      if (diff > 0 && diff < 60000) return diff;
    }
    // Last resort: no data available
    return 0;
  });
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  } as Intl.DateTimeFormatOptions);
}

export function TraceDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  const { currentWorkspaceId: workspaceId } = useAuth();

  const [events, setEvents] = useState<TraceDetailEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!workspaceId || !taskId) return;
    setLoading(true);
    setError(null);
    api
      .getTraceDetail(workspaceId, taskId)
      .then((data) => {
        setEvents(data.events);
        setTotal(data.total);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load trace.'),
      )
      .finally(() => setLoading(false));
  }, [workspaceId, taskId]);

  const summarySteps = useMemo(() => buildStepsFromEvents(events), [events]);
  const durations = useMemo(() => extractDurations(events), [events]);
  const maxDuration = useMemo(
    () => Math.max(...durations, 1),
    [durations],
  );
  const totalDuration = useMemo(
    () => durations.reduce((a, b) => a + b, 0),
    [durations],
  );

  // Determine overall status from events using metadata-aware derivation
  const overallStatus = useMemo(() => deriveOverallStatus(events), [events]);
  const statusStyle = useMemo(() => getStatusStyle(overallStatus), [overallStatus]);

  // Formatted tool name (e.g., "GET api.example.com/path" instead of "http_get")
  const toolName = useMemo(
    () => formatToolName(events.find((e) => e.tool_name)?.tool_name ?? 'Unknown', events),
    [events],
  );

  // Extract context from all TOOL_CALL_RECEIVED events (multi-call tasks)
  const receivedEvents = useMemo(
    () => events.filter(e => e.event_type === 'TOOL_CALL_RECEIVED'),
    [events],
  );
  const capabilities = useMemo(() => {
    const caps = new Set(receivedEvents.map(e => e.metadata?.capability as string).filter(Boolean));
    return [...caps];
  }, [receivedEvents]);
  const platforms = useMemo(() => {
    const plats = new Set(receivedEvents.map(e => e.metadata?.platform as string).filter(Boolean));
    return [...plats];
  }, [receivedEvents]);
  const actorIds = useMemo(() => {
    const ids = new Set(receivedEvents.map(e => e.actor_id).filter(Boolean));
    return [...ids];
  }, [receivedEvents]);

  function toggleEvent(eventId: string) {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }

  if (!workspaceId) {
    return (
      <div className="card p-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
        No workspace found. Please complete onboarding first.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm">
        <Link
          to="/traces"
          className="transition-colors hover:underline"
          style={{ color: 'var(--accent-teal)' }}
        >
          Traces
        </Link>
        <CaretRight size={12} weight="bold" style={{ color: 'var(--text-tertiary)' }} />
        <span className="font-mono" style={{ color: 'var(--text-secondary)' }}>
          {taskId}
        </span>
      </nav>

      {/* Error */}
      {error && (
        <div
          className="rounded-lg px-4 py-3 text-sm"
          style={{
            background: 'var(--color-error-muted)',
            border: '1px solid rgba(248,113,113,0.25)',
            color: 'var(--color-error)',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="spinner-gradient h-8 w-8" />
        </div>
      ) : events.length === 0 ? (
        <div className="card p-12 text-center">
          <TreeStructure
            size={40}
            weight="duotone"
            className="mx-auto mb-3"
            style={{ color: 'var(--text-tertiary)' }}
          />
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            No events found
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            No trace events were recorded for this task.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Header Card */}
          <div className="card p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {toolName}
                </h1>
                <p className="text-sm font-mono" style={{ color: 'var(--text-tertiary)' }}>
                  {taskId}
                </p>
                {/* Context badges */}
                <div className="flex items-center gap-3 pt-1 flex-wrap">
                  {capabilities.length > 0 && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <Shield size={12} weight="duotone" />
                      {capabilities.join(', ')}
                    </span>
                  )}
                  {platforms.length > 0 && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <Globe size={12} weight="duotone" />
                      {platforms.join(', ')}
                    </span>
                  )}
                  {actorIds.length > 0 && (
                    <span className="flex items-center gap-1 text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                      <User size={12} weight="duotone" />
                      {actorIds.join(', ')}
                    </span>
                  )}
                  {receivedEvents.length > 1 && (
                    <span className="flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--accent-violet)' }}>
                      {receivedEvents.length} tool calls
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <Clock size={14} weight="duotone" />
                  <span>{totalDuration}ms total</span>
                </div>
                <span
                  className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
                  style={{
                    background: statusStyle.bg,
                    color: statusStyle.text,
                  }}
                >
                  {statusStyle.label}
                </span>
              </div>
            </div>

            {/* Pipeline Strip (large) */}
            <PipelineStrip steps={summarySteps} size="lg" showLabels />
          </div>

          {/* Truncation warning */}
          {total > 200 && (
            <div
              className="rounded-lg px-4 py-3 text-sm"
              style={{
                background: 'var(--color-warning-muted)',
                border: '1px solid rgba(251,191,36,0.25)',
                color: 'var(--color-warning)',
              }}
            >
              Showing first 200 of {total} events.
            </div>
          )}

          {/* Span Waterfall */}
          <div className="card overflow-hidden">
            {/* Waterfall header */}
            <div
              className="flex items-center px-5 py-3 text-xs font-medium"
              style={{
                borderBottom: '1px solid var(--border)',
                color: 'var(--text-tertiary)',
              }}
            >
              <div className="w-52 shrink-0">Event</div>
              <div className="flex-1">Timeline</div>
              <div className="w-20 shrink-0 text-right">Duration</div>
            </div>

            {/* Waterfall rows */}
            {events.map((event, idx) => {
              const isExpanded = expandedEvents.has(event.event_id);
              const color = getEventColor(event.event_type, event.metadata);
              const bgColor = getEventBgColor(event.event_type, event.metadata);
              const duration = durations[idx];
              const barWidthPercent = Math.max((duration / maxDuration) * 100, 4);
              // Offset start based on cumulative durations
              const cumulativeBefore = durations.slice(0, idx).reduce((a, b) => a + b, 0);
              const offsetPercent = totalDuration > 0 ? (cumulativeBefore / totalDuration) * 60 : 0;
              const hasMetadata = event.metadata && Object.keys(event.metadata).length > 0;
              const eventLabel = EVENT_LABELS[event.event_type] || event.event_type;

              return (
                <div key={event.event_id}>
                  <button
                    onClick={() => toggleEvent(event.event_id)}
                    className="flex w-full items-center px-5 py-3 text-left transition-colors"
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      background: isExpanded ? 'var(--surface-2)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) e.currentTarget.style.background = 'var(--surface-2)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {/* Event name */}
                    <div className="w-52 shrink-0 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: color }}
                        />
                        <span
                          className="text-xs font-medium truncate"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {eventLabel}
                        </span>
                      </div>
                      <span
                        className="ml-4 block text-xs truncate font-mono"
                        style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}
                      >
                        {event.event_type}
                      </span>
                    </div>

                    {/* Timeline bar */}
                    <div className="flex-1 flex items-center">
                      <div className="w-full h-5 relative">
                        <div
                          className="absolute top-0.5 h-4 rounded"
                          style={{
                            left: `${offsetPercent}%`,
                            width: `${Math.min(barWidthPercent * 0.6, 60)}%`,
                            background: color,
                            opacity: 0.75,
                            minWidth: '4px',
                            transition: 'opacity var(--transition-fast)',
                          }}
                        />
                      </div>
                    </div>

                    {/* Duration */}
                    <div
                      className="w-20 shrink-0 text-right text-xs font-mono"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {duration}ms
                    </div>

                    {/* Expand indicator */}
                    {hasMetadata && (
                      <CaretRight
                        size={12}
                        weight="bold"
                        className="ml-2 shrink-0 transition-transform"
                        style={{
                          color: 'var(--text-tertiary)',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      />
                    )}
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div
                      className="px-5 py-4 animate-slide-in"
                      style={{
                        background: 'var(--surface-2)',
                        borderBottom: '1px solid var(--border-subtle)',
                      }}
                    >
                      {/* Event meta row */}
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span
                          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                          style={{ background: bgColor, color }}
                        >
                          {eventLabel}
                        </span>
                        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          <Clock size={12} weight="duotone" />
                          {formatTimestamp(event.timestamp)}
                        </span>
                        {event.actor_id && (
                          <span className="flex items-center gap-1 text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>
                            <User size={12} weight="duotone" />
                            {event.actor_id}
                          </span>
                        )}
                        {event.workspace_id && (
                          <span className="flex items-center gap-1 text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>
                            <Tag size={12} weight="duotone" />
                            {event.workspace_id}
                          </span>
                        )}
                      </div>

                      {/* Structured metadata summary */}
                      {hasMetadata && (() => {
                        const summaryEntries = getEventSummary(event.event_type, event.metadata);
                        return (
                          <div className="space-y-3">
                            {summaryEntries.length > 0 && (
                              <div
                                className="rounded-lg p-4"
                                style={{
                                  background: 'var(--surface-0)',
                                  border: '1px solid var(--border-subtle)',
                                }}
                              >
                                <div className="grid gap-2" style={{ gridTemplateColumns: 'auto 1fr' }}>
                                  {summaryEntries.map((entry, i) => (
                                    <div key={i} className="contents">
                                      <span
                                        className="text-xs font-medium"
                                        style={{ color: 'var(--text-tertiary)' }}
                                      >
                                        {entry.label}
                                      </span>
                                      <span
                                        className="text-xs font-mono break-all"
                                        style={{ color: entry.color || 'var(--text-secondary)' }}
                                      >
                                        {entry.value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Expandable raw JSON for power users */}
                            <details className="group">
                              <summary
                                className="cursor-pointer text-xs select-none"
                                style={{ color: 'var(--text-tertiary)' }}
                              >
                                Raw metadata
                              </summary>
                              <pre
                                className="overflow-x-auto rounded-lg p-4 mt-2 text-xs leading-relaxed font-mono"
                                style={{
                                  background: 'var(--surface-0)',
                                  border: '1px solid var(--border-subtle)',
                                  color: 'var(--text-secondary)',
                                }}
                              >
                                {JSON.stringify(event.metadata, null, 2)}
                              </pre>
                            </details>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
